import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  useMediaQuery,
  Select,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import { useParams } from "react-router-dom";
import ButtonBack from "../../utils/ButtonBack";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import ThemedSelect from "../../utils/ThemedSelect";
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

const labelStyle = {
  display: "block",
  marginBottom: 6,
  fontFamily: FONT,
  fontSize: 12,
  fontWeight: 600,
  color: INK_2,
};

const LeadStatusForProject = ({ projectId }) => {
  const [status, setStatus] = useState("");
  const [statusList, setStatusList] = useState();
  const [meta, setMeta] = useState("");
  const [metaStatusList, setMetaStatusList] = useState();
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const isMobile = useMediaQuery("(max-width: 750px)");
  const [editingId, setEditingId] = useState(null);
const [editValue, setEditValue] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const showModal = (msg, type) => {
    setModalMsg(msg);
    setModalType(type);
    setModalOpen(true);
  };

  const fetchMetaStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/meta-lead/all`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      setMetaStatusList(res.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/lead-status/all?projectId=${projectId}`,
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      setStatusList(res.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetaStatus();
    fetchStatus();
  }, []);

  const handleAdd = async () => {
    if (!status.trim()) return;
    try {
      setBtnLoading(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/lead-status`,
        {
          projectId: projectId,
          status: status,
          meta: meta,
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } },
      );
      fetchStatus();
      showModal("Status added", "success");
      setStatusList((prev) => [...prev, status.trim()]);
    } catch (error) {
      showModal(error?.response?.data?.Exception || "Error while adding status", "error");
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
    setStatus("");
    setMeta("");
  };

  const handleRename = async (statusId) => {
  if (!editValue.trim()) return;

  try {
    await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}/lead-status?statusId=${statusId}&status=${editValue}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
        },
      }
    );

    setStatusList((prev) =>
      prev.map((item) =>
        item.statusId === statusId
          ? { ...item, status: editValue }
          : item
      )
    );

    setEditingId(null);
    setEditValue("");

  } catch (error) {
    console.log(error);
    showModal("Unable to rename status", "error");
  }
};

  return (
    <Box py={2} display="flex" flexDirection="column" gap={2} sx={{ fontFamily: FONT, color: INK }}>
      <Typography
        variant="h5"
        my={1}
        fontWeight={700}
        textAlign={"center"}
        sx={{ fontFamily: FONT, fontSize: { xs: 22, sm: 24 }, fontWeight: 800, letterSpacing: "-0.025em", color: INK, mb: 0 }}
      >
        Manage Lead Status
      </Typography>

      <Box
        className="add-forms"
        sx={{
          width: "100%",
          boxSizing: "border-box",
          background: "#FFFFFF",
          border: `1px solid ${LINE}`,
          borderRadius: "14px",
          p: "16px 18px",
          m: 0,
          boxShadow: "none",
        }}
      >
        <Grid container spacing={1.5} alignItems="flex-end">
          <Grid size={{ xs: 12, sm: 4 }}>
            <label htmlFor="" style={labelStyle}>Lead Status</label>
            <input
              type="text"
              placeholder="Enter lead status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{
                flex: 1,
                display: "block",
                width: "100%",
                boxSizing: "border-box",
                height: 40,
                padding: "0 12px",
                margin: 0,
                background: "#FFFFFF",
                border: `1px solid ${FIELD_EDGE}`,
                borderRadius: "9px",
                fontFamily: FONT,
                fontSize: 14,
                color: INK,
                outline: "none",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <label htmlFor="meta" style={labelStyle}>Meta Status Category</label>
            <ThemedSelect
              name="meta"
              id="meta"
              value={meta}
              onChange={(e) => setMeta(e.target.value)}
              placeholder="--Choose--"
              options={[
                { value: "", label: "--Choose--" },
                ...(metaStatusList || []).map((status) => ({ value: status.status, label: status.status })),
              ]}
              height={40}
            />
          </Grid>
          <Grid
            size={{ xs: 12, sm: 3 }}
            sx={{
              display: { xs: "block", sm: "flex" },
              justifyContent: "flex-start",
              alignItems: "flex-end",
            }}
          >
            <Button
              className="btn-contained"
              fullWidth
              onClick={handleAdd}
              sx={{
                marginTop: "0 !important",
                height: 40,
                borderRadius: "9px !important",
                background: `${PRIMARY} !important`,
                color: "#FFFFFF !important",
                border: "0 !important",
                fontFamily: FONT,
                fontSize: 14,
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { background: "#084767 !important" },
              }}
            >
              Add Status
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* {isMobile ? (
        <Box>
          {statusList?.map((s) => (
            <div key={s.statusId} className="card">
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <Typography
                    fontSize={"22px"}
                    color="#1E1E1E"
                    fontWeight={"600"}
                  >
                    {s.status}
                  </Typography>
                  <Typography
                    fontSize={"12px"}
                    color="#6D7C92"
                    fontWeight={"500"}
                  >
                    ID: {s.statusId}
                  </Typography>
                </div>
                <div className="card-tag">{s.metaLeadStatus?.status}</div>
              </div>

              <hr />
            </div>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>#</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Meta Status</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {statusList?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2}>No statuses added yet.</TableCell>
                </TableRow>
              ) : (
                statusList?.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.status}</TableCell>
                    <TableCell>{item.metaLeadStatus?.status}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )} */}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0,
          background: "#FFFFFF",
          border: `1px solid ${LINE}`,
          borderRadius: "14px",
          overflow: "hidden",
        }}
      >
  {metaStatusList?.map((metaItem, idx) => {
    const metaStatuses =
      statusList?.filter(
        (s) =>
          s.metaLeadStatus?.status === metaItem.status
      ) || [];

    if (metaStatuses.length === 0) return null;

    // Direction C stage palette — same keys as before, light values.
    const colors = [
      { color: "#EAF2FB", borderColor: "#C9DEF3", chipColor: "#EAF2FB", chipBorder: "#1F6FEB", chipText: "#1B4F82" },
      { color: "#EFEDFB", borderColor: "#D3D0F2", chipColor: "#EFEDFB", chipBorder: "#6C5CE7", chipText: "#4A3FBF" },
      { color: "#E6F6F5", borderColor: "#BFE6E3", chipColor: "#E6F6F5", chipBorder: "#17A398", chipText: "#11645E" },
      { color: "#FDF4E1", borderColor: "#F3E0B4", chipColor: "#FDF4E1", chipBorder: "#E8A33D", chipText: "#8A5A08" },
      { color: "#FBEAF3", borderColor: "#F2CDE1", chipColor: "#FBEAF3", chipBorder: "#D6408C", chipText: "#9A2F69" },
      { color: "#E9F7EE", borderColor: "#C4E8D2", chipColor: "#E9F7EE", chipBorder: "#2FA864", chipText: "#1B6B41" },
      { color: "#E4F5FA", borderColor: "#BCE3EF", chipColor: "#E4F5FA", chipBorder: "#1A9CC2", chipText: "#0E5F78" },
      { color: "#FBEDEB", borderColor: "#F2CFCA", chipColor: "#FBEDEB", chipBorder: "#D9493D", chipText: "#A6342A" },
    ];

    const groupStyle =
      colors[idx % colors.length];

    return (
      <Box
        key={metaItem.status}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          borderBottom: "1px solid #F1F4F8",
          "&:last-of-type": { borderBottom: 0 },
          py: 1.75,
          px: 2,
          gap: 2,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* Meta Label */}
        <Box
          sx={{
            minWidth: 150,
            width: { xs: "100%", md: 150 },
            flexShrink: 0,
            background: groupStyle.color,
            border: `1px solid ${groupStyle.borderColor}`,
            borderRadius: "10px",
            p: "9px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
          }}
        >
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              fontFamily: MONO,
              fontSize: "10px",
              fontWeight: 600,
              letterSpacing: "0.11em",
              textTransform: "uppercase",
              color: groupStyle.chipText,
            }}
          >
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: groupStyle.chipBorder }} />
            META
          </Typography>

          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: "14px",
              fontWeight: 800,
              letterSpacing: "-0.01em",
              color: INK,
            }}
          >
            {metaItem.status}
          </Typography>
        </Box>

        {/* Selected Chips */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            pt: { xs: 0, md: "5px" },
          }}
        >
          {metaStatuses.map((status) => (
            <Box
  key={status.statusId}
  sx={{
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    minHeight: 34,
    px: "13px",
    borderRadius: "999px",
    border: `1px solid ${groupStyle.chipBorder}`,
    background: groupStyle.chipColor,
    color: groupStyle.chipText,
    fontFamily: FONT,
    fontSize: "13px",
    fontWeight: 700,
    userSelect: "none",
  }}
>
  <CheckIcon
    sx={{
      fontSize: 14,
      color: groupStyle.chipText,
    }}
  />

  {editingId === status.statusId ? (
    <>
      <input
        autoFocus
        value={editValue}
        onChange={(e) =>
          setEditValue(e.target.value)
        }
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleRename(status.statusId);
          }

          if (e.key === "Escape") {
            setEditingId(null);
            setEditValue("");
          }
        }}
        style={{
          border: "none",
          borderBottom: `1.5px solid ${groupStyle.chipBorder}`,
          outline: "none",
          background: "transparent",
          color: INK,
          fontFamily: FONT,
          fontSize: "13px",
          fontWeight: 600,
          width: "120px",
          marginBottom: '0',
          padding: '2px 0',
        }}
      />

      <DoneIcon
        onClick={() =>
          handleRename(status.statusId)
        }
        sx={{
          fontSize: 16,
          p: "2px",
          borderRadius: "50%",
          cursor: "pointer",
          color: "#FFFFFF",
          background: groupStyle.chipBorder,
        }}
      />

      <CloseIcon
        onClick={() => {
          setEditingId(null);
          setEditValue("");
        }}
        sx={{
          fontSize: 16,
          p: "2px",
          borderRadius: "50%",
          cursor: "pointer",
          color: MUTED,
          "&:hover": { background: "rgba(15,27,42,0.08)" },
        }}
      />
    </>
  ) : (
    <>
      {status.status}

      <EditIcon
        onClick={() => {
          setEditingId(status.statusId);
          setEditValue(status.status);
        }}
        sx={{
          fontSize: 16,
          p: "2px",
          borderRadius: "50%",
          cursor: "pointer",
          opacity: 0.6,
          "&:hover": {
            opacity: 1,
            background: "rgba(15,27,42,0.08)",
          },
        }}
      />
    </>
  )}
</Box>
          ))}
        </Box>
      </Box>
    );
  })}
</Box>

      <ErrorModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        msg={modalMsg}
        type={modalType}
      />
    </Box>
  );
};

export default LeadStatusForProject;